import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import { Modal, Menu, MenuItem } from '../patterns';
import { SprayProgram } from '../../services/sprayService';

interface Props {
  program: SprayProgram;
  onEdit: () => void;
  onClone: () => void;
  onConvert: () => void;
  onDelete: () => void;
  onViewDetails: () => void;
  onUnlock: () => void;
  onRestore: () => void;
}

const ProgramActionsMenu: React.FC<Props> = ({
  program, onEdit, onClone, onConvert, onDelete, onViewDetails, onUnlock, onRestore,
}) => {
  const { t } = useTranslation();
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);

  const status = program.status || 'open';
  const isOpen = status === 'open';
  const isLocked = status === 'locked';
  const isDeleted = status === 'deleted';
  const sessionCount = program.sessionNames?.length ?? 0;
  // Sessions are round-linked: a program with no rounds has nothing to convert.
  const hasRounds = (program.rounds?.length ?? 0) > 0;

  const handleUnlockClick = () => setShowUnlockConfirm(true);
  const handleUnlockConfirm = () => {
    setShowUnlockConfirm(false);
    onUnlock();
  };
  const handleUnlockCancel = () => setShowUnlockConfirm(false);

  return (
    <>
      <Menu
        placement="bottom-end"
        trigger={
          <button
            type="button"
            className="pat-menu__trigger"
            data-testid="program-actions-toggle"
            aria-label={t('spray.programs.actionsMenu')}
          >
            &#8942;
          </button>
        }
      >
        {isOpen && (
          <MenuItem onClick={onEdit}>{t('common.edit')}</MenuItem>
        )}
        {!isOpen && (
          <MenuItem onClick={onViewDetails}>{t('spray.programs.viewDetails')}</MenuItem>
        )}
        <MenuItem onClick={onClone}>{t('spray.programs.clone')}</MenuItem>
        {isLocked && (
          <MenuItem onClick={handleUnlockClick}>{t('spray.programs.unlock')}</MenuItem>
        )}
        {!isDeleted && (
          <MenuItem onClick={onConvert} disabled={!hasRounds}>{t('spray.programs.convertToSession')}</MenuItem>
        )}
        {isDeleted && (
          <MenuItem onClick={onRestore}>{t('spray.programs.restore')}</MenuItem>
        )}
        {!isDeleted && (
          <MenuItem onClick={onDelete} tone="danger">{t('common.delete')}</MenuItem>
        )}
      </Menu>

      <Modal
        show={showUnlockConfirm}
        onHide={handleUnlockCancel}
        size="sm"
        title={t('spray.programs.unlockTitle')}
      >
        <Modal.Body>
          {sessionCount > 0
            ? t('spray.programs.unlockConfirmWithSessions', { count: sessionCount })
            : t('spray.programs.unlockConfirmNoSessions')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleUnlockCancel}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={handleUnlockConfirm}>{t('common.confirm')}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProgramActionsMenu;
